import { defineNuxtPlugin, useRouter, useRuntimeConfig } from '#imports'

interface RedirectRule {
  from: string
  to: string | ((url: string, context: { $config: any; $auth: any }) => string)
  component?: string
}

interface CompiledRule {
  from: RegExp
  to: string | ((url: string, context: { $config: any; $auth: any }) => string)
  component?: string
}

const rules: RedirectRule[] = [
  { from: '^ovatu://customer/([0-9]+)/view', to: '/customer/$1' },
  { from: '^ovatu://customer/([0-9]+)/forms\\?idCustomerForm=([0-9]+)', to: '/customer/form/view?id=$2', component: 'o-panel-link' },
  { from: '^ovatu://customer/([0-9]+)/message\\?idCustomerMessage=([0-9]+)', to: '/message/chat/$1', component: 'o-panel-link' },
  { from: '^ovatu://reservation/([0-9]+)/view\\?date=([0-9]{6})', to: '/appointment/$1?date=$2', component: 'o-panel-link' },
  { from: '^ovatu://order/([0-9]+)/view', to: '/order/$1', component: 'o-panel-link' },
  { from: '^ovatu://waitlist/([0-9]+)/view', to: '/waitlist/$1', component: 'o-panel-link' },
  { from: '^ovatu://review/list', to: '/marketing/reviews' },
  { from: '^ovatu://review/([0-9]+)/view', to: '/marketing/reviews/review?id=$1', component: 'o-panel-link' },
  { from: '^ovatu://messages/list', to: '/message' },
  { from: '^ovatu://settings/sms', to: '/manage/communications/sms' },
  { from: '^ovatu://settings/plan', to: '/account/account' },
  { from: '^ovatu://integration/(\\w+)/authenticate', to: '/integration/$1', component: 'o-panel-link' },
  { from: '^ovatu://settings/verify', to: '/account/verify' },
  { from: '^ovatu://settings/password', to: '/account/team/users' },
  { from: '^ovatu://settings/minisite', to: '/minisite' },
  { from: '^ovatu://settings/policy\\?policy=(\\w+)', to: '/account/security-and-legal/policy?policy=$1' },
  { from: '^ovatu://reservation/list\\?ids=([&\\w]+)', to: '/appointment/list?ids=$1', component: 'o-panel-link' },
  { from: '^ovatu://order/list\\?ids=([&\\w]+)', to: '/order/list?ids=$1', component: 'o-panel-link' },
  { from: '^ovatu://customer/list\\?ids=([&\\w]+)', to: '/customer/list?ids=$1', component: 'o-panel-link' },
  {
    from: '^ovatu://minisite/home',
    to: (_url: string, context: { $config: any; $auth: any }) => {
      const domain = context.$config.bookappDomain
      const slug = context.$auth.info.location.slug

      return 'https://' + slug + '.' + domain
    },
    component: 'a',
  },
]

export interface RedirectPlugin {
  match(url: string): { path: string; component: string }
  route(url: string): void
  readonly rules: CompiledRule[]
}

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  const runtimeConfig = useRuntimeConfig()

  const plugin: RedirectPlugin = {
    match(url) {
      const found = this.rules.find(rule => rule.from.test(url))
      let path = '/'

      if (found) {
        if (found.to instanceof Function) {
          path = found.to(url, {
            $config: runtimeConfig.public,
            $auth: nuxtApp.$auth,
          })
        } else {
          path = url.replace(found.from, found.to)
        }
      }

      const component = found?.component || 'nuxt-link'

      return {
        path,
        component,
      }
    },
    route(url) {
      const { path } = this.match(url)
      router.push(path)
    },

    get rules(): CompiledRule[] {
      return rules.map((rule) => {
        return {
          ...rule,
          from: new RegExp(rule.from),
        }
      })
    },
  }

  nuxtApp.provide('redirect', plugin)
})
